/**
 * Telling a deliberate hover from a passing one, for the Insert preview.
 *
 * - headingToward: the pointer is travelling toward the preview beside the
 *   list (to look at it or click Add), so rows it crosses on the way don't
 *   count. The "menu aim" test used by nested menus: from the last two pointer
 *   positions, check that the slopes to the preview's top-left and bottom-left
 *   corners are narrowing, i.e. the pointer is inside the triangle toward it.
 * - outsideBand: after clicking a row's preview button, small drifts onto the
 *   next row aren't a choice; hover previews resume once the pointer leaves a
 *   band around the clicked row.
 */
export type Point = { x: number; y: number };
export type Edge = { left: number; top: number; bottom: number };
export type Band = { top: number; bottom: number };

const slope = (a: Point, b: Point) => (b.y - a.y) / (b.x - a.x);

export const headingToward = (prev: Point | undefined, cur: Point | undefined, target: Edge | null): boolean => {
  if (!prev || !cur || !target) return false;
  // Moving left or straight up/down, or already over the preview: not on the way to it.
  if (cur.x <= prev.x || cur.x >= target.left) return false;
  const upper = { x: target.left, y: target.top };
  const lower = { x: target.left, y: target.bottom };
  return slope(cur, upper) < slope(prev, upper) && slope(cur, lower) > slope(prev, lower);
};

/** The band around a clicked row: the row plus half a row above and below. */
export const bandAround = (rect: { top: number; bottom: number }): Band => {
  const half = (rect.bottom - rect.top) / 2;
  return { top: rect.top - half, bottom: rect.bottom + half };
};

export const outsideBand = (y: number, band: Band | null) => !band || y < band.top || y > band.bottom;
